import type { RangeKey } from '@/types/admin';
import type { LoyaltyConfig, LoyaltyEntry, ReferralSettings, WalletEntry } from '@/types/admin';
import { ServiceError } from '../../contracts';
import type { AdminLoyaltyService, AdminReferralService, AdminWalletService } from '../../adminContracts';
import { anchorTime, customerBalance, ctx, DAY_MS, delay, hydrateCustomers, matches, nowIso, paginate, rememberKey, saveAdmin, seenKey, uid } from './context';

const invalid = (m: string) => new ServiceError(m, 'INVALID');
const notFound = (w: string) => new ServiceError(`${w} not found`, 'NOT_FOUND');
const days = (r: RangeKey) => (r === '7d' ? 7 : r === '30d' ? 30 : 90);
const dayKey = (t: number) => new Date(t).toISOString().slice(0, 10);

function dayBuckets<T extends Record<string, number>>(anchor: number, n: number, zero: () => T) {
  const map = new Map<string, T & { date: string }>();
  for (let i = n - 1; i >= 0; i--) { const k = dayKey(anchor - i * DAY_MS); map.set(k, { date: k, ...zero() }); }
  return map;
}

/* -------------------------------- Wallet -------------------------------- */
export const adminWallet: AdminWalletService = {
  async overview(range) {
    const { data: d } = ctx('wallet.read', 'wallet');
    const anchor = anchorTime(d);
    const from = anchor - days(range) * DAY_MS;
    const buckets = dayBuckets(anchor, days(range), () => ({ credits: 0, debits: 0 }));
    let topUps = 0, spent = 0, refunds = 0, rewards = 0;
    for (const w of d.wallet) {
      const t = Date.parse(w.createdAt);
      if (t < from) continue;
      if (w.type === 'TOP_UP') topUps += w.amount;
      else if (w.type === 'ORDER_PAYMENT') spent += w.amount;
      else if (w.type === 'REFUND') refunds += w.amount;
      else if (w.type === 'REWARD' || w.type === 'REFERRAL_PAYOUT') rewards += w.amount;
      const b = buckets.get(dayKey(t));
      if (b) { if (w.direction === 'CREDIT') b.credits += w.amount; else b.debits += w.amount; }
    }
    const custs = hydrateCustomers(d);
    return delay({
      totalBalance: custs.reduce((s, c) => s + c.walletBalance, 0), customersWithBalance: custs.filter((c) => c.walletBalance > 0).length,
      topUps, spent, refunds, rewards, series: [...buckets.values()],
    });
  },
  async balances(q) {
    const { data: d } = ctx('wallet.read', 'wallet');
    const last = new Map<string, string>();
    d.wallet.forEach((w) => { if (w.createdAt > (last.get(w.customerId) ?? '')) last.set(w.customerId, w.createdAt); });
    const rows = hydrateCustomers(d).filter((c) => matches(q.q, c.name, c.email))
      .map((c) => ({ customerId: c.id, name: c.name, email: c.email, balance: c.walletBalance, lastActivityAt: last.get(c.id) }))
      .sort((a, b) => b.balance - a.balance);
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async transactions(q) {
    const { data: d } = ctx('wallet.read', 'wallet');
    const rows = d.wallet.filter((w) =>
      (!q.type || q.type === 'all' || w.type === q.type) && (!q.direction || q.direction === 'all' || w.direction === q.direction)
      && (!q.customerId || w.customerId === q.customerId) && matches(q.q, w.customerName, w.reference, w.reason))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async adjust(input) {
    const { actor, data: d } = ctx('wallet.adjust', 'wallet');
    const prior = seenKey(d, input.idempotencyKey);
    if (prior) return delay(d.wallet.find((w) => w.id === prior)!, 300);
    const c = d.customers.find((x) => x.id === input.customerId);
    if (!c) throw notFound('Customer');
    if (!(input.amount > 0) || !Number.isFinite(input.amount)) throw invalid('Enter an amount above zero');
    if (input.amount > 100000) throw invalid('A single adjustment is limited to ₹1,00,000');
    if (input.reason.trim().length < 3) throw invalid('A reason is required for every manual adjustment');
    const bal = customerBalance(d, c.id);
    if (input.direction === 'DEBIT' && input.amount > bal) throw new ServiceError(`Cannot debit ₹${input.amount} — the wallet only holds ₹${bal}.`, 'CONFLICT');
    // Append-only: there is intentionally no way to overwrite a balance or edit a past row.
    const entry: WalletEntry = {
      id: uid('wtx'), tenantId: d.tenantId, customerId: c.id, customerName: c.name, type: 'ADJUSTMENT', direction: input.direction, amount: input.amount,
      balanceAfter: bal + (input.direction === 'CREDIT' ? input.amount : -input.amount), reason: input.reason.trim(), actorName: actor.user.name,
      idempotencyKey: input.idempotencyKey, createdAt: nowIso(),
    };
    d.wallet.push(entry);
    rememberKey(d, input.idempotencyKey, entry.id);
    saveAdmin();
    return delay(entry, 600);
  },
};

/* -------------------------------- Loyalty -------------------------------- */
function validateLoyalty(c: LoyaltyConfig) {
  if (c.earn.pointsPer100 < 0 || c.earn.signupBonus < 0 || c.earn.reviewBonus < 0 || c.earn.referralBonus < 0 || c.earn.minOrder < 0) throw invalid('Earn values cannot be negative');
  if (!(c.redeem.pointValue > 0)) throw invalid('Point value must be above zero');
  if (c.redeem.minRedeem < 0) throw invalid('Minimum redemption cannot be negative');
  if (!(c.redeem.maxRedeemPercent >= 1 && c.redeem.maxRedeemPercent <= 100)) throw invalid('Max redeemable share must be between 1% and 100%');
  if (c.expiryMonths < 0 || c.expiryMonths > 60) throw invalid('Expiry must be between 0 (never) and 60 months');
  if (!c.tiers.length || c.tiers[0].minPoints !== 0) throw invalid('The first tier must start at 0 points');
  c.tiers.forEach((t, i) => {
    if (!t.name.trim()) throw invalid('Every tier needs a name');
    if (t.multiplier < 1 || t.multiplier > 5) throw invalid('Tier multipliers must be between 1× and 5×');
    if (i && t.minPoints <= c.tiers[i - 1].minPoints) throw invalid('Tier thresholds must increase');
  });
  if (new Set(c.tiers.map((t) => t.name.trim().toLowerCase())).size !== c.tiers.length) throw invalid('Tier names must be unique');
}

const tierFor = (cfg: LoyaltyConfig, lifetime: number) => [...cfg.tiers].reverse().find((t) => lifetime >= t.minPoints)?.name ?? cfg.tiers[0].name;

export const adminLoyalty: AdminLoyaltyService = {
  async overview(range) {
    const { data: d } = ctx('loyalty.manage', 'loyalty');
    const anchor = anchorTime(d);
    const from = anchor - days(range) * DAY_MS;
    const buckets = dayBuckets(anchor, days(range), () => ({ earned: 0, redeemed: 0 }));
    let issued = 0, redeemed = 0, expired = 0;
    for (const l of d.loyalty) {
      const t = Date.parse(l.createdAt);
      if (t < from) continue;
      const b = buckets.get(dayKey(t));
      if (l.type === 'EXPIRE') expired += Math.abs(l.points);
      else if (l.points > 0) { issued += l.points; if (b) b.earned += l.points; }
      else if (l.type === 'REDEEM') { redeemed += -l.points; if (b) b.redeemed += -l.points; }
    }
    const custs = hydrateCustomers(d);
    return delay({ issued, redeemed, expired, outstanding: custs.reduce((s, c) => s + c.loyaltyPoints, 0), activeMembers: custs.filter((c) => c.loyaltyPoints > 0).length, series: [...buckets.values()] });
  },
  async getConfig() { return delay(ctx('loyalty.manage', 'loyalty').data.loyaltyConfig); },
  async saveConfig(cfg) {
    const { data: d } = ctx('loyalty.manage', 'loyalty');
    validateLoyalty(cfg);
    d.loyaltyConfig = cfg;
    saveAdmin();
    return delay(cfg, 500);
  },
  async members(q) {
    const { data: d } = ctx('loyalty.manage', 'loyalty');
    const life = new Map<string, number>();
    d.loyalty.forEach((l) => { if (l.points > 0) life.set(l.customerId, (life.get(l.customerId) ?? 0) + l.points); });
    const rows = hydrateCustomers(d).filter((c) => matches(q.q, c.name, c.email))
      .map((c) => ({ customerId: c.id, name: c.name, email: c.email, points: c.loyaltyPoints, tier: tierFor(d.loyaltyConfig, life.get(c.id) ?? 0) }))
      .sort((a, b) => b.points - a.points);
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async transactions(q) {
    const { data: d } = ctx('loyalty.manage', 'loyalty');
    const rows = d.loyalty.filter((l) => (!q.type || q.type === 'all' || l.type === q.type) && (!q.customerId || l.customerId === q.customerId) && matches(q.q, l.customerName, l.description))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async adjust(input) {
    const { actor, data: d } = ctx('loyalty.manage', 'loyalty');
    const prior = seenKey(d, input.idempotencyKey);
    if (prior) return delay(d.loyalty.find((l) => l.id === prior)!, 300);
    const c = d.customers.find((x) => x.id === input.customerId);
    if (!c) throw notFound('Customer');
    if (!Number.isInteger(input.points) || input.points === 0) throw invalid('Points must be a whole number other than zero');
    if (input.reason.trim().length < 3) throw invalid('A reason is required');
    const bal = d.loyalty.filter((l) => l.customerId === c.id).reduce((s, l) => s + l.points, 0);
    if (bal + input.points < 0) throw new ServiceError(`Cannot remove ${Math.abs(input.points)} points — the customer only has ${bal}.`, 'CONFLICT');
    const e: LoyaltyEntry = {
      id: uid('ltx'), tenantId: d.tenantId, customerId: c.id, customerName: c.name, type: 'ADJUSTMENT', points: input.points,
      description: `${input.reason.trim()} (by ${actor.user.name})`, createdAt: nowIso(),
    };
    d.loyalty.push(e);
    rememberKey(d, input.idempotencyKey, e.id);
    saveAdmin();
    return delay(e, 500);
  },
  async listRewards() { return delay([...ctx('loyalty.manage', 'loyalty').data.rewards].sort((a, b) => a.cost - b.cost)); },
  async saveReward(id, input) {
    const { data: d } = ctx('loyalty.manage', 'loyalty');
    if (!input.title.trim()) throw invalid('Reward title is required');
    if (!Number.isInteger(input.cost) || input.cost < 1) throw invalid('Cost must be a whole number of points');
    if (id) {
      const i = d.rewards.findIndex((r) => r.id === id);
      if (i < 0) throw notFound('Reward');
      d.rewards[i] = { ...d.rewards[i], ...input };
      saveAdmin();
      return delay(d.rewards[i], 400);
    }
    const r = { ...input, id: uid('rwd'), tenantId: d.tenantId };
    d.rewards.push(r);
    saveAdmin();
    return delay(r, 400);
  },
  async removeReward(id) {
    const { data: d } = ctx('loyalty.manage', 'loyalty');
    if (!d.rewards.some((r) => r.id === id)) throw notFound('Reward');
    d.rewards = d.rewards.filter((r) => r.id !== id);
    saveAdmin();
    return delay(undefined, 300);
  },
};

/* -------------------------------- Referrals / affiliate -------------------------------- */
function validateReferral(s: ReferralSettings) {
  if (!(s.commissionRate >= 0 && s.commissionRate <= 50)) throw invalid('Commission must be between 0% and 50%');
  if (!(s.cookieDays >= 1 && s.cookieDays <= 90)) throw invalid('Attribution window must be 1–90 days');
  if (s.minPayout < 0) throw invalid('Minimum payout cannot be negative');
  if (!(s.referredDiscount >= 0 && s.referredDiscount <= 100)) throw invalid('Referred-customer discount must be 0–100%');
}

export const adminReferrals: AdminReferralService = {
  async overview() {
    const { data: d } = ctx('referrals.manage', 'affiliate');
    const sum = (st: string) => d.commissions.filter((c) => c.status === st).reduce((s, c) => s + c.amount, 0);
    const clicks = d.referralLinks.reduce((s, l) => s + l.clicks, 0);
    const conversions = d.referralLinks.reduce((s, l) => s + l.conversions, 0);
    return delay({
      links: d.referralLinks.length, clicks, signups: d.referralLinks.reduce((s, l) => s + l.signups, 0), conversions,
      pending: sum('PENDING'), approved: sum('APPROVED'), paid: sum('PAID'), conversionRate: clicks ? (conversions / clicks) * 100 : 0,
    });
  },
  async getSettings() { return delay(ctx('referrals.manage', 'affiliate').data.referralSettings); },
  async saveSettings(s) {
    const { data: d } = ctx('referrals.manage', 'affiliate');
    validateReferral(s);
    d.referralSettings = s;
    saveAdmin();
    return delay(s, 500);
  },
  async links(q) {
    const { data: d } = ctx('referrals.manage', 'affiliate');
    return delay(paginate(d.referralLinks.filter((l) => matches(q.q, l.code, l.ownerName)), q.page, q.pageSize ?? 10));
  },
  async setLinkActive(id, active) {
    const { data: d } = ctx('referrals.manage', 'affiliate');
    const l = d.referralLinks.find((x) => x.id === id);
    if (!l) throw notFound('Referral link');
    l.active = active;
    saveAdmin();
    return delay(l, 250);
  },
  async referred(q) {
    const { data: d } = ctx('referrals.manage', 'affiliate');
    return delay(paginate(d.referred.filter((r) => matches(q.q, r.customerName, r.referrerName)).sort((a, b) => b.date.localeCompare(a.date)), q.page, q.pageSize ?? 10));
  },
  async commissions(q) {
    const { data: d } = ctx('referrals.manage', 'affiliate');
    const rows = d.commissions.filter((c) => (!q.status || q.status === 'all' || c.status === q.status) && matches(q.q, c.referrerName, c.orderNumber))
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return delay(paginate(rows, q.page, q.pageSize ?? 10));
  },
  async actOnCommission(id, action) {
    const { actor, data: d } = ctx('referrals.manage', 'affiliate');
    const c = d.commissions.find((x) => x.id === id);
    if (!c) throw notFound('Commission');
    const allowed = { APPROVE: ['PENDING'], REJECT: ['PENDING', 'APPROVED'], PAY: ['APPROVED'] }[action];
    if (!allowed.includes(c.status)) throw new ServiceError(`A ${c.status.toLowerCase()} commission cannot be ${action === 'PAY' ? 'paid' : action.toLowerCase() + 'd'}.`, 'CONFLICT');
    if (action === 'PAY') {
      if (d.referralSettings.payoutDestination === 'WALLET') {
        if (!d.features.wallet) throw new ServiceError('Payouts go to customer wallets, but the wallet module is off. Switch the payout destination or enable wallet.', 'CONFLICT');
        const bal = customerBalance(d, c.referrerId);
        d.wallet.push({
          id: uid('wtx'), tenantId: d.tenantId, customerId: c.referrerId, customerName: c.referrerName, type: 'REFERRAL_PAYOUT', direction: 'CREDIT',
          amount: c.amount, balanceAfter: bal + c.amount, reference: c.orderNumber, reason: 'Referral commission', actorName: actor.user.name, createdAt: nowIso(),
        });
      }
      c.status = 'PAID';
      c.paidAt = nowIso();
    } else c.status = action === 'APPROVE' ? 'APPROVED' : 'REJECTED';
    saveAdmin();
    return delay(c, 500);
  },
};
